import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {environment} from '../../environments/environment';
import {JwtHelperService} from '@auth0/angular-jwt';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, catchError, filter, map, Observable, take, throwError} from 'rxjs';
import {CustomHttpResponse} from '../model/custom-http-response';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  host = environment.apiUrl;
  private token: string | null = null;
  private refreshToken: string | null = null;
  private loggedInUsername: string | null = null;
  private tokenLoaded = false;
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient,
              private jwtHelper: JwtHelperService,
              @Inject(PLATFORM_ID) private platformId: Object) {
    this.loadTokenOnce()
  }

  private loadTokenOnce(): void {
    if (this.tokenLoaded || !isPlatformBrowser(this.platformId)) return;

    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      this.token = storedToken;
      try {
        const decoded = this.jwtHelper.decodeToken(storedToken);
        this.loggedInUsername = decoded.sub || null;
      } catch (e) {
      }
    }

    const storedRefresh = localStorage.getItem('refreshToken');
    if (storedRefresh) {
      this.refreshToken = storedRefresh;
    }

    this.tokenLoaded = true;
  }

  public getToken(): string | null {
    return this.token;
  }

  public getRefreshToken(): string | null {
    return this.refreshToken;
  }

  public login(email: string, password: string): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.post<CustomHttpResponse>(
      `${this.host}/v1/customer/auth/login`,
      {email, password},
      {observe: 'response'}
    );
  }


  public saveToken(token: string | null | undefined): void {
    this.token = token ?? null;
    if (isPlatformBrowser(this.platformId)) {
      token ? localStorage.setItem('token', token) : localStorage.removeItem('token');
    }
  }

  public saveRefreshToken(token: string | null | undefined): void {
    this.refreshToken = token ?? null;
    if (isPlatformBrowser(this.platformId)) {
      if (token) {
        localStorage.setItem('refreshToken', token);
      } else {
        localStorage.removeItem('refreshToken');
      }
    }
  }

  public isLoggedIn(): boolean {
    if (!this.token) return false;
    if (this.jwtHelper.isTokenExpired(this.token)) {
      return !!this.refreshToken;
    }
    return true;
  }


  public getLoggedInUsername(): string | null {
    return this.loggedInUsername;
  }

  private isTokenValid(): boolean {
    if (this.token != null && this.token != '') {
      const {sub} = this.jwtHelper.decodeToken(this.token);
      if (sub != null && sub !== '' && !this.jwtHelper.isTokenExpired(this.token)) {
        this.loggedInUsername = sub;
        return true;
      }
    }
    return false;
  }

  public doRefreshToken(): Observable<any> {
    return this.http.post<any>(
      `${this.host}/v1/customer/auth/token/refresh`,
      {refreshToken: this.refreshToken},
      {observe: 'response'}
    );
  }

  public handleTokenRefresh(): Observable<string | null> {
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1)
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return this.doRefreshToken().pipe(
      map((response: HttpResponse<any>) => {
        const newAccess = response.body?.data?.access ?? null;
        const newRefresh = response.body?.data?.refresh ?? null;
        if (newAccess) this.saveToken(newAccess);
        if (newRefresh) this.saveRefreshToken(newRefresh);

        this.isRefreshing = false;
        this.refreshSubject.next(newAccess);
        return newAccess;
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.refreshSubject.next(null);
        this.clearSession();
        return throwError(() => err);
      })
    );
  }

  public clearSession(): void {
    this.token = null;
    this.refreshToken = null;
    this.loggedInUsername = null;
    this.isRefreshing = false;
    this.refreshSubject.next(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
  }

  public loadToken(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.token = localStorage.getItem('token');
      this.refreshToken = localStorage.getItem('refreshToken');
    }
  }

  public isLogin(): boolean {
    this.loadToken();
    if (this.token) return true;
    return false;
  }

  registerCustomer(redirectUri: string, request: any): Observable<HttpResponse<CustomHttpResponse>> {
    const params = new HttpParams().set('redirectUri', redirectUri);
    return this.http.post<CustomHttpResponse>(
      `${this.host}/v1/customer/auth/register`,
      request,
      {
        observe: 'response',
        params
      }
    );
  }

  logout(): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http
      .post<CustomHttpResponse>(
        `${this.host}/v1/customer/auth/logout`, '', {observe: 'response'}
      );
  }

  resetPassword(resetURI: string, email: string): Observable<HttpResponse<CustomHttpResponse>> {
    const params = new HttpParams().set('resetURI', resetURI);
    return this.http.post<CustomHttpResponse>(`${this.host}/v1/customer/auth/password/rest`, {email}, {
      observe: 'response',
      params
    })
  }

  updatePassword(body: any): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(`${this.host}/v1/customer/auth/password/rest`, body, {observe: 'response'})
  }

}
